import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/use-toast';
import { mutationLimiter, checkRateLimit } from '@/lib/rateLimiter';
import type { Tables, TablesUpdate } from '@/integrations/supabase/types';

export type Profile = Tables<'profiles'>;
export type ProfileUpdate = TablesUpdate<'profiles'>;

export function useProfile() {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      return data as Profile;
    },
    enabled: !!user,
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (updates: Partial<ProfileUpdate>) => {
      checkRateLimit(mutationLimiter, 'updateProfile');
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', user.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      toast({ title: 'Profile updated' });
    },
    onError: (e) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });
}

export function useUploadAvatar() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (file: File) => {
      checkRateLimit(mutationLimiter, 'uploadAvatar');
      if (!user) throw new Error('Not authenticated');

      // Validate file
      if (!file.type.startsWith('image/')) throw new Error('File must be an image');
      if (file.size > 2 * 1024 * 1024) throw new Error('Image must be less than 2MB');

      const ext = file.name.split('.').pop() || 'jpg';
      const filePath = `${user.id}/avatar.${ext}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (updateError) throw updateError;

      return publicUrl;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      toast({ title: 'Avatar updated' });
    },
    onError: (e) => toast({ title: 'Upload failed', description: e.message, variant: 'destructive' }),
  });
}
